/**
 * Utilities for CSV export with Windows compatibility
 */

export interface CSVExportOptions {
  filename: string
  headers: string[]
  data: Record<string, any>[]
  delimiter?: string
  fieldMapping?: string[] // Optional mapping to specify which fields to export in order
}

/**
 * Sanitizes a field value for CSV export, handling special characters
 * and ensuring Windows compatibility
 */
const FORMULA_PREFIX = /^[=+\-@\t\r]/
const NUMERIC_VALUE = /^-?\d+([.,]\d+)*$/

export const sanitizeCSVField = (field: any): string => {
  // Keep 0/false; only null/undefined become empty
  let fieldStr = String(field ?? '').trim()

  // Normalize line breaks and tabs so each record stays on one line
  fieldStr = fieldStr.replace(/\r\n|\r|\n|\t/g, ' ')

  // Prevent CSV/formula injection when the file is opened in Excel
  if (FORMULA_PREFIX.test(fieldStr) && !NUMERIC_VALUE.test(fieldStr)) {
    fieldStr = `'${fieldStr}`
  }

  // Always wrap fields in quotes for better Windows compatibility
  return `"${fieldStr.replace(/"/g, '""')}"`
}

/**
 * Sanitizes filename to be compatible with Windows file system
 */
export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[<>:"/\\|?*]/g, '_') // Replace Windows forbidden characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[áàâãä]/gi, 'a')
    .replace(/[éèêë]/gi, 'e')
    .replace(/[íìîï]/gi, 'i')
    .replace(/[óòôõö]/gi, 'o')
    .replace(/[úùûü]/gi, 'u')
    .replace(/[ç]/gi, 'c')
    .replace(/[ñ]/gi, 'n')
}

/**
 * Exports data to CSV with optimal Windows compatibility
 */
export const exportToCSV = (options: CSVExportOptions): void => {
  const { filename, headers, data, delimiter = ';', fieldMapping } = options

  // Sanitize headers (remove special characters for better compatibility)
  const sanitizedHeaders = headers.map(header =>
    header
      .replace(/[áàâãä]/gi, 'a')
      .replace(/[éèêë]/gi, 'e')
      .replace(/[íìîï]/gi, 'i')
      .replace(/[óòôõö]/gi, 'o')
      .replace(/[úùûü]/gi, 'u')
      .replace(/[ç]/gi, 'c')
      .replace(/[ñ]/gi, 'n')
  )

  // Process data rows
  const csvRows = data.map(row => {
    let rowValues: string[]

    if (fieldMapping && fieldMapping.length > 0) {
      // Use specific field mapping if provided
      rowValues = fieldMapping.map(key => sanitizeCSVField(row[key]))
    } else {
      // Use all object keys if no mapping provided
      rowValues = Object.keys(row).map(key => sanitizeCSVField(row[key]))
    }

    return rowValues.join(delimiter)
  })

  // Wrap headers in quotes for consistency
  const quotedHeaders = sanitizedHeaders.map(header => `"${header}"`)

  const csvContent = [quotedHeaders.join(delimiter), ...csvRows].join('\r\n') // Use Windows line endings for better compatibility

  // Add BOM for UTF-8 to ensure proper encoding in Windows Excel
  const BOM = '\uFEFF'
  const csvWithBOM = BOM + csvContent

  // Use specific MIME type for better Windows compatibility
  const blob = new Blob([csvWithBOM], {
    type: 'text/csv;charset=utf-8;'
  })

  const link = document.createElement('a')

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    const sanitizedFilename = sanitizeFilename(filename)

    link.setAttribute('href', url)
    link.setAttribute('download', sanitizedFilename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the object URL to prevent memory leaks
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }
}
